/**
 * The Academy lessons, at Ben Gurion (LLBG): arrivals land on runway 12, departures leave from
 * runway 26. Positions and headings come from the airport data, so the numbers in the text match
 * the scope. Every lesson is written and tested at low realism (pilots act at once, no wind);
 * a session uses the player's own level.
 */
import { hdg3 } from '../hints';
import type { Lesson, StepContext, StepResult } from './lesson';
import { asked, backNm, everSawFor, find, flying, ft, gone, magneticVector, offBy, offsetNm, onDownwind, onFinal, pairIn, runway, sawFor, spoken } from './lesson-helpers';
import { TOWER_ARRIVAL, TOWER_DEPARTURE, TOWER_RECOVERY, TOWER_CROSSING, TOWER_FLOW, TOWER_WAKE } from './tower-lessons';
import { APPROACH_AND_HOLD, ARRIVAL_BANK, DEPARTURE_STREAM, EMERGENCY_PRIORITY, FIRST_CONTACT, GO_AROUND_RECOVERY, WAKE_SPACING } from './lessons-advanced';

export const RADAR_BASICS: Lesson = {
  id: 'radar-basics',
  title: 'Radar basics',
  summary: 'Read a data block, then turn, descend, slow and send an aircraft direct to a fix.',
  airport: 'llbg',
  position: 'approach',
  tier: 'beginner',
  commands: ['heading', 'altitude', 'speed', 'direct'],
  briefing: {
    chartIds: ['approach-ils-rwy-12', 'arrival-llbg-standard-arrival-chart-instrument-rnav-star-icao-rwys-12-30-goded2-ninet1'],
    situation: 'Ben Gurion approach · landing 12, departing 26',
    scene: [
      'ELY101 is on the GODED 2 arrival, 20 miles northwest at 8,000 ft, and has just joined approach. Its arrival ends at GODED, where the runway 12 ILS begins.',
      'There is no competing traffic yet. Use a vector, descent, speed, and the filed fix to build a stable route instead of issuing disconnected commands.',
    ],
    objectives: ['Turn ELY101 onto a heading', 'Descend it to 5,000 ft', 'Slow it to 210 knots', 'Send it direct to GODED'],
    notes: ['Every instruction starts with the callsign.', 'The sim waits after each step, so there is no time pressure.'],
  },
  realism: 'low',
  build(airport) {
    const cs = 'ELY101', say = spoken(airport, cs);
    return {
      setup(sim) {
        sim.addAircraft({ kind: 'arrival', callsign: cs, type: 'B738', star: 'GODED2', at: 'GODED', nmBefore: 4, altitudeFt: 8000, iasKt: 250 });
      },
      steps: [
        {
          title: 'Give a heading',
          text: `${cs} is the square northwest of the airport. Its data block shows the callsign, then altitude in hundreds of feet (080 is 8,000 ft) and ground speed in knots; the line ahead is where it will be in a minute. Every command starts with the callsign. C with three digits is a heading: turn ${cs} left to heading 090.`,
          hint: `${cs} C 090`,
          say: `${say}, turn left heading zero niner zero`,
          solution: [`${cs} C 090`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return a.assigned.headingDeg === 90 && flying(airport, a, 90) < 2 ? 'pass' : null;
          },
          testTimeoutS: 60,
        },
        {
          title: 'Descend',
          text: `C with one or two digits is an altitude in thousands of feet. Clear ${cs} down to 5,000 ft and watch its altitude count down, with an arrow while it descends.`,
          hint: `${cs} C 5`,
          say: `${say}, descend and maintain 5,000`,
          solution: [`${cs} C 5`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return a.assigned.altitudeFt === 5000 && Math.abs(a.altitudeFt - 5000) < 50 ? 'pass' : null;
          },
          testTimeoutS: 240,
        },
        {
          title: 'Slow down',
          text: `S sets the speed in knots. Arrivals slow down before the approach; ask ${cs} for 210 knots. Speed changes take a little while.`,
          hint: `${cs} S 210`,
          say: `${say}, reduce speed to 210 knots`,
          solution: [`${cs} S 210`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return a.assigned.speedKt === 210 && Math.abs(a.iasKt - 210) < 3 ? 'pass' : null;
          },
          testTimeoutS: 120,
        },
        {
          title: 'Direct to a fix',
          text: `The triangles are fixes: named points on the map. C with a fix name sends the aircraft straight to it, and after the fix it keeps its heading. A heading took ${cs} off its arrival; send it direct to GODED to put it back on it, where the runway 12 approach begins.`,
          hint: `${cs} C GODED`,
          say: `${say}, proceed direct GODED`,
          solution: [`${cs} C GODED`],
          check: (ctx) => {
            if (ctx.all.some((e) => e.type === 'reachedFix' && e.ac.callsign === cs && e.fix === 'GODED')) return 'pass';
            return find(ctx, cs) ? null : gone(ctx, cs);
          },
          testTimeoutS: 900,
        },
      ],
    };
  },
};

export const LANDING: Lesson = {
  id: 'landing',
  position: 'approach',
  title: 'Cleared for approach',
  summary: 'Vector an arrival off its STAR: downwind, base, a shallow intercept and the ILS, then hand it to tower.',
  airport: 'llbg',
  tier: 'beginner',
  commands: ['altitude', 'heading', 'approach', 'contactTower'],
  briefing: {
    chartIds: ['approach-ils-rwy-12', 'arrival-llbg-standard-arrival-chart-instrument-star-icao-rwy-12-ammit-1b-salam-2b'],
    situation: 'Ben Gurion approach · landing 12, arrival from the south',
    scene: [
      'ELY202 flew the SALAM 2B arrival from the south. The chart ends at GEMDA in radar vectors, and the previous controller turned it onto a left downwind for runway 12: northeast of the field at 5,000 ft.',
      'You own the rest of its way in. Descend it, let the downwind run out past the field, turn it onto a base leg, then cut in to the localizer at 30° with the ILS clearance, and hand it to tower.',
    ],
    objectives: ['Descend on the downwind', 'Turn base about 12 miles out', 'Cut in to the localizer and clear the ILS', 'Transfer it to tower and observe the landing'],
    notes: ['Join the localizer outside BG94D, 9.4 miles out, no steeper than 30°.', 'A landing clearance belongs to tower. Transfer an established arrival before short final.'],
  },
  realism: 'low',
  build(airport) {
    const cs = 'ELY202', say = spoken(airport, cs);
    const rw = runway(airport, '12');
    // Left of the final (northeast): base and the intercept both turn left, toward the centreline.
    const base = magneticVector(airport, rw.headingDeg + 90), intercept = magneticVector(airport, rw.headingDeg + 30);
    const limitFt = airport.elevationFt + 3000;
    return {
      setup(sim) { onDownwind(sim, airport, rw, cs, 'B738', 1, 5, 5000, 210); },
      steps: [
        {
          title: 'Down to the joining level',
          text: `${cs} is on the downwind, the runway 12 final off its left side. Descend it to 3,000 ft now: it has to be at or below the glideslope, about 3,000 ft here, before it joins the final.`,
          hint: `${cs} C 3`,
          say: `${say}, descend and maintain 3,000`,
          solution: [`${cs} C 3`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return (a.assigned.altitudeFt ?? Infinity) <= limitFt ? 'pass' : null;
          },
          testTimeoutS: 30,
        },
        {
          title: 'Let the downwind run',
          text: `Keep ${cs} on the downwind until it is about 12 miles out: turning base earlier leaves no room to join the final outside BG94D.`,
          hint: 'No command yet. Watch it pass abeam 12 miles on the final.',
          say: 'Nothing to say yet.',
          solution: [],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return backNm(rw, a) >= 12 || (a.assigned.headingDeg !== undefined && offBy(a.assigned.headingDeg, base) < 30) ? 'pass' : null;
          },
          testTimeoutS: 600,
        },
        {
          title: 'Turn base',
          text: `Turn ${cs} left onto a base leg, heading ${hdg3(base)}, square to the final. Hold it there until it is about two miles from the centreline.`,
          hint: `${cs} C ${hdg3(base)} L`,
          say: `${say}, turn left heading ${hdg3(base)}`,
          solution: [`${cs} C ${hdg3(base)} L`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            if (a.approach) return 'pass';
            return a.assigned.headingDeg !== undefined && flying(airport, a, a.assigned.headingDeg) < 5 && Math.abs(offsetNm(rw, a)) <= 2.5 ? 'pass' : null;
          },
          testTimeoutS: 300,
        },
        {
          title: 'Cut in and clear the ILS',
          text: `Turn ${cs} left to ${hdg3(intercept)}, a 30° cut onto the final course, and clear the ILS in the same call. It captures the localizer outside BG94D and follows the glideslope down.`,
          hint: `${cs} C ${hdg3(intercept)} L I 12`,
          say: `${say}, turn left heading ${hdg3(intercept)}, cleared ILS approach runway 12`,
          solution: [`${cs} C ${hdg3(intercept)} L I 12`],
          check: (ctx) => {
            if (everSawFor(ctx, 'established', cs)) return 'pass';
            if (sawFor(ctx, 'missedIntercept', cs)) return { fail: `${cs} went through the final without capturing it. Cut in no steeper than 30°, from about two miles out.` };
            if (sawFor(ctx, 'goAround', cs)) return { fail: `${cs} went around. Try again and let it settle on the final.` };
            return find(ctx, cs) ? null : gone(ctx, cs);
          },
          testTimeoutS: 600,
        },
        {
          title: 'Transfer to tower',
          text: `${cs} is established. Transfer it to tower; the tower colleague checks the runway and issues its landing clearance.`,
          hint: `${cs} CTW`, say: `${say}, contact tower`, solution: [`${cs} CTW`],
          check: (ctx) => ctx.all.some((e) => e.type === 'academyTowerHandoff' && e.ac.callsign === cs) ? 'pass' : find(ctx, cs) ? null : gone(ctx, cs), testTimeoutS: 10,
        },
        {
          title: 'See it land', text: 'Monitor the final and confirm touchdown.',
          hint: 'Watch the aircraft touch down.', say: 'Nothing to say; tower has the aircraft.', solution: [],
          check: (ctx) => everSawFor(ctx, 'touchdown', cs) ? 'pass' : sawFor(ctx, 'goAround', cs) ? { fail: 'Tower sent the arrival around. Rebuild the approach and spacing.' } : find(ctx, cs) ? null : gone(ctx, cs), testTimeoutS: 900,
        },
      ],
    };
  },
};

export const DEPARTURES: Lesson = {
  id: 'departures',
  title: 'Departures',
  summary: 'Receive an airborne departure from tower, then climb and route it to its exit fix.',
  airport: 'llbg',
  position: 'approach',
  tier: 'beginner',
  commands: ['altitude', 'contact'],
  briefing: {
    chartIds: ['departure-sid-rwy-26'],
    situation: 'Ben Gurion approach · departing 26',
    scene: [
      'Tower has launched ISR303 from runway 26 on the SUVAS 1E departure and handed it to you climbing through 1,000 ft. The SID takes it over MATOV, ORLEV and TAPUZ to KESEF, where it leaves your airspace.',
      'Approach owns its climb through the terminal area; the crew flies the SID on its own. Give it a level high enough for the handoff, then watch it go.',
    ],
    objectives: ['Climb ISR303 after the tower handoff', 'Watch it fly the SUVAS 1E', 'See it hand off at KESEF'],
    notes: ['A departure is handed off within a mile of its exit fix at 4,000 ft above the field or higher.'],
  },
  realism: 'low',
  build(airport) {
    const cs = 'ISR303', exit = 'KESEF', say = spoken(airport, cs);
    const handoffFt = Math.ceil((airport.elevationFt + 4000) / 1000) * 1000;
    if (!airport.fixes.has(exit)) throw new Error(`${airport.icao} has no fix ${exit}`);
    return {
      setup(sim) {
        const ac = sim.addAircraft({ kind: 'departure', callsign: cs, type: 'A320', runway: '26', requested: exit });
        sim.academyLaunchDeparture(cs, 5000);
        for (let i = 0; i < 480 && (ac.onGround || ac.altitudeFt < airport.elevationFt + 1000); i++) sim.step(.25);
      },
      steps: [
        {
          title: 'Climb the departure',
          text: `${cs} checks in after tower's takeoff clearance. Climb it to 7,000 ft so it has room to leave via ${exit}. The exit handoff needs ${ft(handoffFt)} or higher.`,
          hint: `${cs} C 7`,
          say: `${say}, climb and maintain 7,000`,
          solution: [`${cs} C 7`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return (a.assigned.altitudeFt ?? 0) >= handoffFt ? 'pass' : null;
          },
          testTimeoutS: 5,
        },
        {
          title: 'Watch the SID',
          text: `${cs} flies the SUVAS 1E on its own: MATOV, ORLEV, TAPUZ, then ${exit}. When it passes within a mile of ${exit} at ${ft(handoffFt)} or higher it is handed off, worth 10 points. A direct would only be a shortcut, and this SID keeps it clear of the arrivals.`,
          hint: `No command. Watch ${cs} follow the SID to ${exit}.`,
          say: 'Nothing to say; the crew flies the SID.',
          solution: [],
          check: (ctx) => {
            // Lesson-wide: a player who sent it direct early can have the handoff behind them.
            if (everSawFor(ctx, 'handoff', cs)) return 'pass';
            if (sawFor(ctx, 'exited', cs)) return { fail: `${cs} left the airspace without a handoff. Keep it heading for ${exit} and high enough.` };
            return find(ctx, cs) ? null : gone(ctx, cs);
          },
          testTimeoutS: 1200,
        },
      ],
    };
  },
};

export const SEPARATION: Lesson = {
  id: 'separation',
  title: 'Separation',
  summary: 'Two arrival streams pass each other near the final: descend the lower aircraft first, grant the requested level, then land the straight-in and turn the downwind in behind it.',
  airport: 'llbg',
  position: 'approach',
  tier: 'mid',
  commands: ['altitude', 'heading', 'approach', 'contactTower'],
  briefing: {
    chartIds: ['approach-ils-rwy-12', 'arrival-llbg-standard-arrival-chart-instrument-star-icao-rwy-12-ammit-1b-salam-2b', 'arrival-llbg-standard-arrival-chart-instrument-rnav-star-icao-rwys-12-30-goded2-ninet1'],
    situation: 'Ben Gurion approach · landing 12, two arrival streams',
    scene: [
      'ELY401 came up the SALAM 2B from the south and is on a tight left downwind for runway 12 at 6,000 ft, two and a half miles off the final. AIZ402 is on the GODED 2 from the northwest at 9,000 ft, straight in along the final, and requests descent to 6,000.',
      'The two will pass abeam each other less than three miles apart. The request is reasonable, but ELY401 holds the level AIZ402 wants: give the lower aircraft somewhere lower to go first. After they pass, AIZ402 is ahead on the final and lands first; ELY401 extends its downwind and turns in behind it.',
    ],
    objectives: ['Hear the request and read both levels', 'Descend the lower arrival first', 'Then approve the requested descent', 'Clear AIZ402, the straight-in, for the ILS', 'Extend ELY401 and turn it in behind', 'Transfer both and see them land'],
    notes: [
      'Keep three miles laterally or 1,000 ft vertically. The amber alert is a last warning, not a cue to start planning.',
      'Cleared for the ILS on the GODED 2, a crew flies the chart from GODED. Behind another arrival, five miles is a good gap to turn in on.',
    ],
  },
  realism: 'low',
  build(airport) {
    const down = 'ELY401', straight = 'AIZ402', sayStraight = spoken(airport, straight), sayDown = spoken(airport, down);
    const rw = runway(airport, '12');
    const base = magneticVector(airport, rw.headingDeg + 90), intercept = magneticVector(airport, rw.headingDeg + 30);
    /** Separation lost at any point ends the lesson, whichever step it happens in. */
    const lost = (ctx: StepContext): StepResult | undefined =>
      ctx.all.some((e) => e.type === 'conflictStart' && pairIn(e, down, straight)) ? { fail: `${down} and ${straight} lost separation. Descend the lower aircraft before the upper one comes down to its level, and turn the downwind in only once the straight-in is well ahead.` } : undefined;
    const both = (ctx: StepContext): StepResult | undefined => (!find(ctx, down) ? gone(ctx, down) : !find(ctx, straight) ? gone(ctx, straight) : undefined);
    const guard = (ctx: StepContext) => lost(ctx) ?? both(ctx);
    return {
      setup(sim) {
        onDownwind(sim, airport, rw, down, 'B738', 6, 2.5, 6000, 210);
        const b = sim.addAircraft({ kind: 'arrival', callsign: straight, type: 'E195', star: 'GODED2', at: 'GODED', nmBefore: 4.5, altitudeFt: 9000, iasKt: 210 });
        b.targetIasKt = b.assigned.speedKt = 210;
      },
      steps: [
        {
          title: 'A reasonable request',
          text: `${straight} asks to descend to 6,000 ft. ${down}, on the downwind, is at exactly that level, and the two are about to pass abeam each other less than three miles apart. Read the levels before answering.`,
          hint: 'Read the request and the two data blocks.',
          say: 'Listen to the request; no clearance yet.',
          solution: [],
          onEnter: (sim) => sim.academyCall(straight, `${straight}, request descent to 6,000 for the arrival`, 'descent'),
          check: (ctx) => ctx.all.some((e) => e.type === 'academyCall' && e.callsign === straight && e.request === 'descent') ? 'pass' : both(ctx) ?? null,
          testTimeoutS: 10,
        },
        {
          title: 'Make room below',
          text: `Descend ${down} first, to 4,000 ft: below the level ${straight} wants before they pass, and on its way down to the final.`,
          hint: `${down} C 4`,
          say: `${sayDown}, descend and maintain 4,000`,
          solution: [`${down} C 4`],
          check: (ctx) => guard(ctx) ?? ((find(ctx, down)!.assigned.altitudeFt ?? Infinity) <= 5000 ? 'pass' : null),
          testTimeoutS: 60,
        },
        {
          title: 'Answer the descent request',
          text: `${down} is on its way down. Now ${straight} can have the 6,000 ft it asked for: they pass at least a thousand feet apart.`,
          hint: `${straight} C 6`,
          say: `${sayStraight}, descend and maintain 6,000`,
          solution: [`${straight} C 6`],
          check: (ctx) => {
            const r = guard(ctx);
            if (r !== undefined) return r;
            return find(ctx, straight)!.assigned.altitudeFt === 6000 ? 'pass' : null;
          },
          testTimeoutS: 60,
        },
        {
          title: `${straight} lands first`,
          text: `${straight} is straight in on the final, ahead of ${down} once they have passed. Clear it for the ILS on its arrival; the crew flies the chart from GODED onto the localizer.`,
          hint: `${straight} I 12`,
          say: `${sayStraight}, cleared ILS approach runway 12`,
          solution: [`${straight} I 12`],
          check: (ctx) => {
            const r = guard(ctx);
            if (r !== undefined) return r;
            if (everSawFor(ctx, 'established', straight)) return 'pass';
            return sawFor(ctx, 'missedIntercept', straight) ? { fail: `${straight} did not capture the localizer. Clear the ILS while it is still on its arrival.` } : null;
          },
          testTimeoutS: 600,
        },
        {
          title: `Transfer ${straight}`,
          text: `${straight} is established. Transfer it to tower for its landing clearance.`,
          hint: `${straight} CTW`, say: `${sayStraight}, contact tower`, solution: [`${straight} CTW`],
          check: (ctx) => ctx.all.some((e) => e.type === 'academyTowerHandoff' && e.ac.callsign === straight) ? 'pass' : guard(ctx) ?? null, testTimeoutS: 10,
        },
        {
          title: 'Extend the downwind',
          text: `Keep ${down} on the downwind until ${straight} is about five miles further down the final, and ${down} is at least 12 miles out. Turning in sooner puts it on top of ${straight}.`,
          hint: 'No command yet. Watch the gap along the final grow.',
          say: 'Nothing to say yet.',
          solution: [],
          check: (ctx) => {
            const r = lost(ctx);
            if (r) return r;
            const a = find(ctx, down), b = find(ctx, straight);
            if (!a) return gone(ctx, down);
            const gap = b ? backNm(rw, a) - backNm(rw, b) : Infinity;
            return (backNm(rw, a) >= 12 && gap >= 5) || (a.assigned.headingDeg !== undefined && offBy(a.assigned.headingDeg, base) < 30) ? 'pass' : null;
          },
          testTimeoutS: 600,
        },
        {
          title: 'Turn base',
          text: `Turn ${down} left onto a base leg, heading ${hdg3(base)}, and down to 3,000 ft.`,
          hint: `${down} C ${hdg3(base)} L C 3`,
          say: `${sayDown}, turn left heading ${hdg3(base)}, descend and maintain 3,000`,
          solution: [`${down} C ${hdg3(base)} L C 3`],
          check: (ctx) => {
            const r = lost(ctx);
            if (r) return r;
            const a = find(ctx, down);
            if (!a) return gone(ctx, down);
            return a.approach || (a.assigned.headingDeg !== undefined && flying(airport, a, a.assigned.headingDeg) < 5 && Math.abs(offsetNm(rw, a)) <= 2.5) ? 'pass' : null;
          },
          testTimeoutS: 300,
        },
        {
          title: `Clear ${down} for the ILS`,
          text: `Turn ${down} left to ${hdg3(intercept)}, a 30° cut, and clear the ILS in the same call. Then transfer it to tower once it is established.`,
          hint: `${down} C ${hdg3(intercept)} L I 12`,
          say: `${sayDown}, turn left heading ${hdg3(intercept)}, cleared ILS approach runway 12`,
          solution: [`${down} C ${hdg3(intercept)} L I 12`],
          check: (ctx) => {
            const r = lost(ctx);
            if (r) return r;
            if (everSawFor(ctx, 'established', down)) return 'pass';
            if (sawFor(ctx, 'missedIntercept', down)) return { fail: `${down} went through the final without capturing it. Cut in no steeper than 30°.` };
            return find(ctx, down) ? null : gone(ctx, down);
          },
          testTimeoutS: 600,
        },
        {
          title: 'Both on the ground',
          text: `Transfer ${down} to tower, then watch both land. Descending the lower one first is what let everyone have what they asked for.`,
          hint: `${down} CTW`,
          say: `${sayDown}, contact tower`,
          solution: [`${down} CTW`],
          check: (ctx) => {
            const r = lost(ctx);
            if (r) return r;
            if (ctx.all.some((e) => e.type === 'goAround' && [down, straight].includes(e.ac.callsign))) return { fail: 'One of the pair went around. Keep more room between them on the final.' };
            return [down, straight].every((cs) => everSawFor(ctx, 'touchdown', cs)) ? 'pass' : null;
          },
          testTimeoutS: 1200,
        },
      ],
    };
  },
};

export const READBACK: Lesson = {
  id: 'readback-correction', title: 'Hear it, check it, correct it',
  summary: 'Catch a deliberately wrong readback, correct it, and verify what the pilot actually has.',
  airport: 'llbg',
  position: 'approach',
  tier: 'mid',
  commands: ['heading', 'altitude', 'query-assignedHeading'],
  trainingReadback: true,
  briefing: {
    chartIds: [],
    situation: 'Ben Gurion approach · unreliable readback',
    scene: [
      'ELY505 is arriving beside a protected departure corridor. Radio interference makes the first readback of each new numeric clearance unreliable.',
      'A wrong readback can send the aircraft toward that corridor or through the level you intended. Catch it while the flight is still well clear.',
    ],
    objectives: ['Give a heading and listen to the readback', 'Correct the wrong one', 'Ask the pilot what heading they have', 'Catch a garbled altitude the same way'],
    notes: ['Compare the controller line with the pilot line in the radio log; audio is optional.'],
  },
  realism: 'low',
  build(airport) {
    const cs = 'ELY505', say = spoken(airport, cs);
    return {
      setup(sim) {
        sim.addAircraft({ kind: 'arrival', callsign: cs, type: 'B738', star: 'GODED2', at: 'GODED', altitudeFt: 6000, iasKt: 210 });
      },
      steps: [
        { title: 'Listen to the readback', text: `Clear ${cs} to heading 090, away from the departure corridor. Interference corrupts the first readback; compare it with your clearance before the aircraft turns.`, hint: `${cs} C 090`, say: `${say}, turn left heading zero niner zero`, solution: [`${cs} C 090`], check: (ctx) => sawFor(ctx, 'command', cs) ? 'pass' : null, testTimeoutS: 5 },
        { title: 'Correct the heading', text: 'The readback did not match 090. Repeat the correct callsign and heading. Watch the assigned heading return to 090. In demanding play, listening and correcting matters as much as the first clearance.', hint: `${cs} C 090`, say: `${say}, negative — turn left heading zero niner zero`, solution: [`${cs} C 090`], check: (ctx) => sawFor(ctx, 'command', cs) && ctx.sim.find(cs)?.assigned.headingDeg === 90 ? 'pass' : null, testTimeoutS: 5 },
        {
          title: 'Verify what they have',
          text: `Correcting a readback tells you what you said. Asking tells you what the pilot has. Ask ${cs} for its assigned heading — the answer comes from the aircraft, not from your own memory of the clearance.`,
          hint: `${cs} Q assignedHeading`,
          say: `${say}, say assigned heading`,
          solution: [`${cs} Q assignedHeading`],
          check: (ctx) => (asked(ctx, cs, 'assignedHeading') ? 'pass' : find(ctx, cs) ? null : gone(ctx, cs)),
          testTimeoutS: 10,
        },
        {
          title: 'It happens with numbers too',
          text: `Headings are not the only thing that gets misheard. Descend ${cs} to 5,000 ft; this crew will garble the first readback of that too. Catch it and repeat the clearance until the assigned altitude is right.`,
          hint: `${cs} C 5`,
          say: `${say}, descend and maintain 5,000`,
          solution: [`${cs} C 5`, `${cs} C 5`],
          // The garble is armed once per clearance, so this step re-arms it as it begins.
          onEnter: (sim) => sim.enableTrainingReadback(),
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            return sawFor(ctx, 'command', cs) && a.assigned.altitudeFt === 5000 ? 'pass' : null;
          },
          testTimeoutS: 10,
        },
      ],
    };
  },
};

export const PILOT_CONVERSATION: Lesson = {
  id: 'pilot-conversation', title: 'Talk to your pilot',
  summary: 'Check the crew’s instructions, handle a speed refusal, and give a revised plan in one call.',
  airport: 'llbg',
  position: 'approach',
  tier: 'mid',
  commands: ['query-instructions', 'speed', 'query-assignedSpeed', 'query-intentions', 'heading', 'altitude'],
  briefing: {
    chartIds: ['approach-ils-rwy-12'],
    situation: 'Ben Gurion approach · landing 12',
    scene: [
      'ELY321 is ten miles out on the runway 12 ILS. You are checking its assigned speed because the closure rate looks high.',
      'The crew will report rough air ahead and request vectors away from it. Ask what it needs, then take it off the approach with a safe vector and level.',
    ],
    objectives: ['Check the crew’s instructions', 'Assign and verify a speed', 'Hear the crew’s request', 'Ask intentions', 'Give a heading and a level in one transmission'],
    notes: ['A question never changes the flight. A clearance always does.'],
  },
  realism: 'low',
  build(airport) {
    const cs = 'ELY321';
    const say = spoken(airport, cs);
    const rw = runway(airport, '12');
    const vector = hdg3(magneticVector(airport, rw.headingDeg + 90));
    const lost = (ctx: StepContext): StepResult => (find(ctx, cs) ? null : gone(ctx, cs));
    return {
      setup(sim) {
        const p = onFinal(rw, 10, 0);
        sim.addAircraft({ kind: 'arrival', callsign: cs, type: 'B738', lat: p.lat, lon: p.lon, altitudeFt: airport.elevationFt + 2500, headingDeg: rw.headingDeg, iasKt: 210 });
        sim.command(`${cs} I 12`);
      },
      steps: [
        {
          title: 'Ask for the clearance',
          text: `Questions report what the pilot knows and never change the flight. ${cs} is 10 miles out on the runway 12 final. Say “${say}, what are your current instructions”. The reply is the clearance the pilot understood, which is what you check when a readback sounded wrong.`,
          hint: `${cs} Q instructions`,
          say: `${say}, say your current instructions`,
          solution: [`${cs} Q instructions`],
          check: (ctx) => (asked(ctx, cs, 'instructions') ? 'pass' : lost(ctx)),
          testTimeoutS: 5,
        },
        {
          title: 'Slow down and confirm',
          text: `Assign a speed, then ask for it back: “${say}, reduce speed to 180”, then “${say}, say assigned speed”. “Say speed” reports the airspeed flown right now, which lags behind the assignment.`,
          hint: `${cs} S 180, then ${cs} Q assignedSpeed`,
          say: `${say}, reduce speed to 180 — then: ${say}, say assigned speed`,
          solution: [`${cs} S 180`, `${cs} Q assignedSpeed`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            const set = ctx.events.findIndex((e) => e.type === 'command' && e.ac.callsign === cs && e.instructions.some((i) => i.kind === 'speed'));
            return set >= 0 && (a.assigned.speedKt ?? Infinity) < 210 && asked(ctx, cs, 'assignedSpeed', set) ? 'pass' : null;
          },
          testTimeoutS: 5,
        },
        {
          title: 'A new request',
          text: `${cs} reports rough air ahead and asks for vectors away from it. The flight remains on its current path until you amend the plan.`,
          hint: 'Listen to the crew’s report.',
          say: 'The pilot has requested vectors; no new clearance yet.',
          solution: [],
          onEnter: (sim) => sim.academyCall(cs, `${cs}, rough air ahead, request vectors clear of it`, 'vectors'),
          check: (ctx) => {
            return ctx.all.some((e) => e.type === 'academyCall' && e.callsign === cs && e.request === 'vectors') ? 'pass' : lost(ctx);
          },
          testTimeoutS: 5,
        },
        {
          title: 'Ask for intentions',
          text: `Ask ${cs} for intentions before replacing its clearance. It wants vectors clear of the rough air and another approach later.`,
          hint: `${cs} Q intentions`,
          say: `${say}, say intentions`,
          solution: [`${cs} Q intentions`],
          check: (ctx) => (asked(ctx, cs, 'intentions') ? 'pass' : lost(ctx)),
          testTimeoutS: 5,
        },
        {
          title: 'Vectors in one call',
          text: `Take ${cs} off the approach and away from the turbulence. Give heading ${vector} and 4,000 ft in one transmission, then check that the request has cleared.`,
          hint: `${cs} C ${vector} C 4`,
          say: `${say}, turn right heading ${vector}, climb and maintain 4,000`,
          solution: [`${cs} C ${vector} C 4`],
          check: (ctx) => {
            const a = find(ctx, cs);
            if (!a) return gone(ctx, cs);
            const oneCall = ctx.events.some((e) => e.type === 'command' && e.ac.callsign === cs && e.instructions.some((i) => i.kind === 'heading' || i.kind === 'direct') && e.instructions.some((i) => i.kind === 'altitude'));
            return oneCall && !a.pilot.request && !a.pilot.recovery ? 'pass' : null;
          },
          testTimeoutS: 60,
        },
      ],
    };
  },
};

/**
 * The training route, in the order players fly it: each tier is a leg, and inside a leg the radar
 * exercises come before the tower ones. The Academy screen numbers lessons by this order.
 */
export const LESSONS: readonly Lesson[] = [
  // Beginner
  FIRST_CONTACT, RADAR_BASICS, LANDING, DEPARTURES, TOWER_DEPARTURE,
  // Intermediate
  SEPARATION, READBACK, APPROACH_AND_HOLD, GO_AROUND_RECOVERY, PILOT_CONVERSATION, TOWER_ARRIVAL, TOWER_CROSSING,
  // Advanced
  DEPARTURE_STREAM, ARRIVAL_BANK, WAKE_SPACING, EMERGENCY_PRIORITY, TOWER_RECOVERY, TOWER_WAKE, TOWER_FLOW,
];
